/* iter-p3-c — Author's personal inbox surface.
 *
 * Two-pane dialog: thread list + selected thread messages with a
 * reply composer.  Everything is scoped to the current user via
 * /api/inbox/my* endpoints (aliases resolve to `<slug>@mail...`).
 *
 * Opens from Settings → Language & Inbox or from the workspace
 * header (via a dedicated button).
 */
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, Send, Copy, Inbox, Paperclip, PenSquare } from 'lucide-react';
import { toast } from 'sonner';
import { inboxApi } from '@/lib/api';
import QuotaPill from '@/components/QuotaPill';

function fmt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

// iter-p3-e — Sanitise inbound HTML before rendering (XSS defence).
const SAFE_HTML_CONFIG = {
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction'],
  ALLOW_DATA_ATTR: false,
};
function sanitizeHtml(dirty) {
  try { return DOMPurify.sanitize(dirty || '', SAFE_HTML_CONFIG); }
  catch { return ''; }
}

export default function MyInboxPanel({ open, onOpenChange }) {
  const [alias, setAlias]     = useState('');
  const [threads, setThreads] = useState([]);
  const [busy, setBusy]       = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply]     = useState('');
  const [sending, setSending] = useState(false);
  // iter-p3-f — Compose from scratch.
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo,   setComposeTo]   = useState('');
  const [composeSubj, setComposeSubj] = useState('');
  const [composeText, setComposeText] = useState('');
  const [composing, setComposing]     = useState(false);

  const loadAll = () => {
    setBusy(true);
    Promise.all([
      inboxApi.myAlias().then((r) => setAlias(r?.alias || '')).catch(() => {}),
      inboxApi.threads('all', 'open', 100)
        .then((r) => setThreads(r?.threads || []))
        .catch(() => toast.error('Could not load your inbox.')),
    ]).finally(() => setBusy(false));
  };
  useEffect(() => { if (open) loadAll(); }, [open]);

  const openThread = (tid) => {
    setSelectedId(tid);
    setThread(null); setMessages([]);
    inboxApi.thread(tid)
      .then((r) => {
        setThread(r?.thread || null);
        setMessages(r?.messages || []);
        if (r?.thread?.unread) {
          inboxApi.markRead(tid).then(loadAll).catch(() => {});
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load thread.'));
  };

  const sendReply = async () => {
    if (!selectedId || !reply.trim()) return;
    setSending(true);
    try {
      await inboxApi.reply(selectedId, { text: reply });
      toast.success('Reply sent');
      setReply('');
      openThread(selectedId);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reply.'); }
    finally { setSending(false); }
  };

  const copyAlias = async () => {
    if (!alias) return;
    try { await navigator.clipboard.writeText(alias); toast.success('Alias copied'); }
    catch { /* clipboard blocked */ }
  };

  const sendCompose = async () => {
    if (!composeTo.trim() || !composeSubj.trim() || !composeText.trim()) {
      toast.error('Fill in recipient, subject, and message.');
      return;
    }
    setComposing(true);
    try {
      const r = await inboxApi.compose({
        to: composeTo.trim(),
        subject: composeSubj.trim(),
        text: composeText,
      });
      toast.success('Message sent — appearing in your inbox shortly.');
      setComposeOpen(false);
      setComposeTo(''); setComposeSubj(''); setComposeText('');
      loadAll();
      if (r?.thread_id) openThread(r.thread_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send.');
    } finally { setComposing(false); }
  };

  const unread = useMemo(() => threads.filter((t) => t.unread).length, [threads]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="my-inbox-panel">
        <DialogHeader className="border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Inbox className="h-4 w-4 text-primary" />
            My Inbox
            {unread > 0 && <span className="ml-2 rounded bg-primary px-1.5 py-0.5 text-[10.5px] text-primary-foreground">{unread}</span>}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 text-[11.5px]">
            Your mail alias:
            <code className="rounded-sm border border-border bg-background px-1.5 py-px font-mono-stat text-[11px]" data-testid="my-inbox-alias">
              {alias || (busy ? 'loading…' : 'not set — enable Email Routing first')}
            </code>
            {alias && (
              <button onClick={copyAlias} className="rounded-sm border border-border px-1 py-px hover:bg-secondary" data-testid="my-inbox-copy-alias">
                <Copy className="inline h-3 w-3" />
              </button>
            )}
            <span className="ml-auto"><QuotaPill /></span>
            <button
              type="button"
              onClick={() => setComposeOpen(true)}
              data-testid="my-inbox-compose-open"
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11.5px] hover:bg-secondary"
            >
              <PenSquare className="h-3 w-3" /> New message
            </button>
          </DialogDescription>
        </DialogHeader>
        <div className="grid h-[520px] grid-cols-[280px_1fr]">
          <div className="overflow-auto border-r border-border" data-testid="my-inbox-thread-list">
            {busy && <div className="p-3 text-[12px] text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin" /> Loading…</div>}
            {!busy && threads.length === 0 && (
              <div className="p-3 text-[12px] text-muted-foreground" data-testid="my-inbox-empty">
                Nothing here yet. Share your alias so replies land in this inbox.
              </div>
            )}
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => openThread(t.id)}
                data-testid={`my-inbox-thread-${t.id}`}
                className={`block w-full border-b border-border px-3 py-2 text-left hover:bg-panel-header/30 ${selectedId === t.id ? 'bg-panel-header/50' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate text-[12.5px] font-semibold">{t.peer_name || t.peer_email}</div>
                  <div className="shrink-0 text-[10.5px] text-muted-foreground">{fmt(t.last_message_at)}</div>
                </div>
                <div className="mt-0.5 truncate text-[11.5px] text-foreground/80">{t.subject || '(no subject)'}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                  <span className="rounded bg-panel-header px-1 py-px">{t.folder || 'other'}</span>
                  {t.unread && <span className="ml-auto rounded bg-primary px-1.5 text-primary-foreground">NEW</span>}
                </div>
              </button>
            ))}
          </div>

          <div className="flex flex-col overflow-hidden">
            {!thread ? (
              <div className="p-4 text-[12px] text-muted-foreground">Select a thread to view messages.</div>
            ) : (
              <>
                <div className="border-b border-border bg-panel-header/40 px-3 py-2">
                  <div className="truncate text-[13px] font-semibold">{thread.subject || '(no subject)'}</div>
                  <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {thread.peer_name || ''} &lt;{thread.peer_email}&gt;
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-3" data-testid="my-inbox-messages">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className={`mb-3 rounded-md border p-2.5 ${m.direction === 'outbound' ? 'border-emerald-400/40 bg-emerald-50/40' : 'border-border bg-panel-header/20'}`}
                      data-testid={`my-inbox-message-${m.id}`}
                    >
                      <div className="mb-1 flex items-center justify-between text-[10.5px] text-muted-foreground">
                        <span>{m.direction === 'outbound' ? 'You →' : `${m.from_name || m.from_addr} →`}</span>
                        <span>{fmt(m.at)}</span>
                      </div>
                      {m.html ? (
                        <div className="prose prose-sm max-w-none text-[12.5px]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.html) }} />
                      ) : (
                        <div className="whitespace-pre-wrap text-[12.5px]">{m.text || '(empty body)'}</div>
                      )}
                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`my-inbox-attachments-${m.id}`}>
                          {m.attachments.map((a, i) => (
                            <a
                              key={i}
                              href={a.url ? `${process.env.REACT_APP_BACKEND_URL || ''}${a.url}` : '#'}
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-1 text-[11px] hover:bg-secondary"
                              data-testid={`my-inbox-attachment-${m.id}-${i}`}
                            >
                              <Paperclip className="h-3 w-3" />
                              {a.filename}
                              {a.size ? <span className="text-muted-foreground">({Math.round(a.size / 1024)}KB)</span> : null}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="border-t border-border p-3">
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply…"
                    className="min-h-[90px] text-[12.5px]"
                    data-testid="my-inbox-reply-text"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} className="gap-1.5" data-testid="my-inbox-send">
                      {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Send reply
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
      {/* iter-p3-f — Compose-from-scratch panel. */}
      {composeOpen && (
        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent className="max-w-lg" data-testid="my-inbox-compose">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[13px]">
                <PenSquare className="h-3.5 w-3.5" /> New message
              </DialogTitle>
              <DialogDescription className="text-[11px]">
                Sends from your inbox — replies come back to your <code>@mail</code> alias.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11.5px]">To</Label>
                <Input value={composeTo} onChange={(e) => setComposeTo(e.target.value)}
                    placeholder="reader@example.com"
                    className="mt-1 h-8 text-[12.5px]" data-testid="my-inbox-compose-to" />
              </div>
              <div>
                <Label className="text-[11.5px]">Subject</Label>
                <Input value={composeSubj} onChange={(e) => setComposeSubj(e.target.value)}
                    placeholder="Thanks for reading!"
                    className="mt-1 h-8 text-[12.5px]" data-testid="my-inbox-compose-subject" />
              </div>
              <div>
                <Label className="text-[11.5px]">Message</Label>
                <Textarea value={composeText} onChange={(e) => setComposeText(e.target.value)}
                    placeholder="Write your message…"
                    className="mt-1 min-h-[140px] text-[12.5px]" data-testid="my-inbox-compose-text" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setComposeOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={sendCompose} disabled={composing || !composeTo.trim() || !composeSubj.trim() || !composeText.trim()} data-testid="my-inbox-compose-send" className="gap-1.5">
                  {composing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Send
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

// Named export for a compact toolbar-style unread bell.
export function InboxBell({ onOpen }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => {
      inboxApi.threads('all', 'open', 100)
        .then((r) => { if (alive) setCount((r?.threads || []).filter((t) => t.unread).length); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative inline-flex h-7 w-7 items-center justify-center rounded-sm border border-border bg-background hover:bg-secondary"
      data-testid="my-inbox-bell"
      title="My inbox"
    >
      <Mail className="h-3.5 w-3.5" />
      {count > 0 && (
        <span className="absolute -right-1 -top-1 rounded-full bg-primary px-1 text-[9.5px] leading-tight text-primary-foreground" data-testid="my-inbox-bell-count">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
}
